import React from 'react';
import { convertNumberToDesiredFormat } from 'helpers/transformHelpers';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { formatEnumValue } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/helpers';
import { ApplicationMethod } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { applicationMethodOptions } from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import formatRegistrationNumber from 'helpers/formatRegistrationNumber';
import { View, Text } from '@react-pdf/renderer';
import { OrientationOptions } from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';
import { capitalizeName } from 'helpers/textHelpers';
import { TableHeaderItem } from '../../components/Table/TableHeader';
import {
  displayText,
  EMPTY_TEXT,
  formatTextWithSmartLineBreaks,
  hyphenateText
} from '../formatHelpers';
import { FlexTableProps } from '../../components/FlexTable/types';
import { en, Locale } from '../../locales';
import { ReiphisDataItem, getReiPhiTexts } from './reiPhiHelpers';

// Type definitions for product data
interface ActiveIngredient {
  name: string;
}

interface TargetPest {
  pestName: string;
}

interface WorkerProtection {
  productName?: string;
  key?: string;
  signalword?: string;
  rup?: string;
  ppe?: string;
  ppereentry?: string;
}

export interface ProductTableData {
  name?: string;
  registrationnumber?: string;
  applicationMethod?: ApplicationMethod;
  targetPests?: TargetPest[];
  activeIngredients?: ActiveIngredient[];
  reiphisData?: ReiphisDataItem[];
  appliedAreaValue?: number;
  appliedAreaUnit?: string;
  ratePerAreaValue?: number;
  ratePerAreaUnit?: string;
  ratePerTankValue?: number;
  ratePerTankUnit?: string;
  totalProductValue?: number;
  totalProductUnit?: string;
  workerProtection?: WorkerProtection;
}

// Common style constants
const BASE_FONT_SIZE = 10;
const CENTER_ALIGN = 'center';
const RIGHT_ALIGN = 'right';

// Column configuration for both orientations
type ColumnConfig = {
  key: string;
  labels: {
    portrait: string;
    landscape: string;
  };
  maxWidths: {
    portrait: string;
    landscape: string;
  };
  textAlign?: 'left' | 'center' | 'right';
  flex?: number;
  marginLeft?: 'auto';
  isTankColumn?: boolean;
  shouldHyphenate?: boolean;
};

const getColumnConfigs = (
  locale: Locale,
  isLatamSpanish: boolean = false
): ColumnConfig[] => [
  {
    key: 'name',
    labels: {
      portrait: locale.products.withLineBreaks.name,
      landscape: locale.products.name
    },
    maxWidths: { portrait: '70px', landscape: '140px' }
  },
  {
    key: 'epaNumber',
    labels: {
      portrait: locale.products.withLineBreaks.epa_number,
      landscape: locale.products.epa_number
    },
    maxWidths: { portrait: '54px', landscape: '80px' }
  },
  {
    key: 'appMethod',
    labels: {
      portrait: isLatamSpanish
        ? locale.products.application_method
        : locale.products.withLineBreaks.application_method,
      landscape: isLatamSpanish
        ? locale.products.withLineBreaks.application_method
        : locale.products.application_method
    },
    maxWidths: { portrait: '58px', landscape: '90px' },
    shouldHyphenate: true
  },
  {
    key: 'pest',
    labels: {
      portrait: locale.products.withLineBreaks.target_pest,
      landscape: locale.products.target_pest
    },
    maxWidths: { portrait: '50px', landscape: '80px' },
    shouldHyphenate: true
  },
  {
    key: 'rei',
    labels: {
      portrait: locale.products.rei,
      landscape: locale.products.rei
    },
    maxWidths: { portrait: '26px', landscape: '26px' },
    textAlign: CENTER_ALIGN
  },
  {
    key: 'phi',
    labels: {
      portrait: locale.products.phi,
      landscape: locale.products.phi
    },
    maxWidths: { portrait: '26px', landscape: '26px' },
    textAlign: CENTER_ALIGN
  },
  {
    key: 'appArea',
    labels: {
      portrait: locale.products.withLineBreaks.application_area,
      landscape: locale.products.application_area
    },
    maxWidths: { portrait: '55px', landscape: '80px' },
    textAlign: RIGHT_ALIGN,
    marginLeft: 'auto'
  },
  {
    key: 'ratePerAc',
    labels: {
      portrait: locale.products.withLineBreaks.rate_per_acre,
      landscape: locale.products.rate_per_acre
    },
    maxWidths: { portrait: '65px', landscape: '80px' },
    textAlign: RIGHT_ALIGN
  },
  {
    key: 'ratePerTank',
    labels: {
      portrait: locale.products.withLineBreaks.rate_per_tank,
      landscape: locale.products.rate_per_tank
    },
    maxWidths: { portrait: '67px', landscape: '80px' },
    textAlign: RIGHT_ALIGN,
    isTankColumn: true
  },
  {
    key: 'totalProduct',
    labels: {
      portrait: locale.products.withLineBreaks.total_product,
      landscape: locale.products.total_product
    },
    maxWidths: { portrait: '55px', landscape: '80px' },
    textAlign: RIGHT_ALIGN
  }
];

// Helper to get adjusted maxWidth based on tankInformation status
const getAdjustedMaxWidth = (
  config: ColumnConfig,
  orientation: OrientationOptions,
  hasTankInformation: boolean
): string => {
  const baseMaxWidth = config.maxWidths[orientation];

  // Increase maxWidths for specific columns when tankInformation is off
  if (!hasTankInformation) {
    const columnsToExpand = [
      'name',
      'epaNumber',
      'appMethod',
      'pest',
      'appArea',
      'ratePerAc',
      'totalProduct'
    ];
    if (columnsToExpand.includes(config.key)) {
      // Extract numeric value and unit
      const regex = /^(\d{1,6})(px)$/;
      const match = regex.exec(baseMaxWidth);
      if (match) {
        const numericValue = Number.parseInt(match[1], 10);
        const unit = match[2];

        // Increase by different amounts based on column
        const increase = config.key === 'name' ? 35 : 20;

        return `${numericValue + increase}${unit}`;
      }
    }
  }

  return baseMaxWidth;
};

// Helper to create header item styles
const createHeaderStyles = (
  config: ColumnConfig,
  orientation: OrientationOptions,
  hasTankInformation: boolean
): TableHeaderItem['styles'] => {
  const baseStyles: TableHeaderItem['styles'] = {
    fontSize: BASE_FONT_SIZE,
    width: getAdjustedMaxWidth(config, orientation, hasTankInformation)
  };

  if (config.flex) {
    baseStyles.flex = config.flex;
  }

  if (config.textAlign) {
    baseStyles.textAlign = config.textAlign;
  }

  if (config.marginLeft) {
    baseStyles.marginLeft = config.marginLeft;
  }

  return baseStyles;
};

// Helper to create header items from configuration
const createHeaderItems = (
  orientation: OrientationOptions,
  hasTankInformation: boolean,
  locale: Locale,
  isLatamSpanish: boolean = false
): TableHeaderItem[] => {
  const columnConfigs = getColumnConfigs(locale, isLatamSpanish);
  return columnConfigs
    .filter(config => hasTankInformation || !config.isTankColumn)
    .map(config => ({
      label: config.labels[orientation],
      styles: createHeaderStyles(config, orientation, hasTankInformation)
    }));
};

export const getProductHeaderItems = (
  hasTankInformation: boolean,
  orientation: OrientationOptions = 'portrait',
  locale: Locale = en,
  isLatamSpanish: boolean = false
): TableHeaderItem[] => {
  return createHeaderItems(
    orientation,
    hasTankInformation,
    locale,
    isLatamSpanish
  );
};

export const mapWorkerProtectionToTable = (products: ProductTableData[]) =>
  products.map(product => product.workerProtection);

// Helper to create cell styles from column config
const createCellStyles = (
  config: ColumnConfig,
  orientation: OrientationOptions,
  hasTankInformation: boolean
): Record<string, any> => {
  const styles: Record<string, any> = {
    width: getAdjustedMaxWidth(config, orientation, hasTankInformation)
  };

  if (config.flex) {
    styles.flex = config.flex;
  }

  if (config.textAlign) {
    styles.textAlign = config.textAlign;
  }

  if (config.marginLeft) {
    styles.marginLeft = config.marginLeft;
  }

  return styles;
};

// Helper to create a cell with consistent structure
const createCell = (
  key: string,
  label: React.ReactNode,
  config: ColumnConfig,
  orientation: OrientationOptions,
  hasTankInformation: boolean
) => ({
  key,
  label,
  styles: createCellStyles(config, orientation, hasTankInformation),
  shouldHyphenate: config.shouldHyphenate
});

// Helper to format EPA number with orientation-specific line break limits
const formatEPANumberForTable = (
  epaNumber: string,
  orientation: OrientationOptions
): string => {
  const formattedEpaNumber = formatRegistrationNumber(epaNumber);

  if (!formattedEpaNumber) return EMPTY_TEXT;

  return formatTextWithSmartLineBreaks(
    formattedEpaNumber,
    orientation === 'portrait' ? 8 : 15
  );
};

// Create a map of column keys to configs for efficient lookup
const createColumnConfigMap = (locale: Locale) => {
  const configs = getColumnConfigs(locale);
  return configs.reduce((acc, config) => {
    acc[config.key] = config;
    return acc;
  }, {} as Record<string, ColumnConfig>);
};

export const getProductTableRows = (
  products: ProductTableData[],
  hasTankInformation: boolean,
  cropNames: string[],
  orientation: OrientationOptions = 'portrait',
  locale: Locale = en,
  isLatamSpanish: boolean = false,
  cropIds: string[] = []
): FlexTableProps['tableRows'] => {
  const COLUMN_CONFIG_MAP = createColumnConfigMap(locale);
  const COLUMN_CONFIGS = getColumnConfigs(locale);

  const productRows: FlexTableProps['tableRows'] = products.map(product => {
    const { reiText, phiText } = getReiPhiTexts(
      product.reiphisData,
      cropNames,
      cropIds
    );

    // Map product data to cells using column configuration
    const cellBuilders: Record<string, () => ReturnType<typeof createCell>> = {
      name: () => {
        const config = COLUMN_CONFIG_MAP.name;
        const activeIngredients = (product.activeIngredients || [])
          .map(activeIngredient =>
            formatTextWithSmartLineBreaks(activeIngredient.name, 18)
          )
          .join(', ');

        return createCell(
          'name',
          <View style={{ display: 'flex', flexDirection: 'column' }}>
            <Text
              style={{ fontWeight: 'semibold' }}
              hyphenationCallback={word => hyphenateText(word)}
            >
              {displayText(product.name)}
            </Text>
            {activeIngredients.length > 0 ? (
              <Text style={{ fontStyle: 'italic' }}>{activeIngredients}</Text>
            ) : (
              <Text />
            )}
          </View>,
          config,
          orientation,
          hasTankInformation
        );
      },
      epaNumber: () => {
        const config = COLUMN_CONFIG_MAP.epaNumber;
        return createCell(
          'epaNumber',
          formatEPANumberForTable(
            product.registrationnumber || '',
            orientation
          ),
          config,
          orientation,
          hasTankInformation
        );
      },
      appMethod: () => {
        const config = COLUMN_CONFIG_MAP.appMethod;
        return createCell(
          'appMethod',
          formatEnumValue(
            product.applicationMethod,
            ApplicationMethod,
            applicationMethodOptions
          ),
          config,
          orientation,
          hasTankInformation
        );
      },
      pest: () => {
        const config = COLUMN_CONFIG_MAP.pest;
        const targetPestNames: string[] =
          product?.targetPests?.map(pest => capitalizeName(pest.pestName)) ||
          [];

        const pestNamesText =
          targetPestNames.length > 0
            ? targetPestNames.filter(Boolean).join(', ')
            : EMPTY_TEXT;

        return createCell(
          'pest',
          displayText(pestNamesText),
          config,
          orientation,
          hasTankInformation
        );
      },
      rei: () => {
        const config = COLUMN_CONFIG_MAP.rei;
        return createCell(
          'rei',
          reiText,
          config,
          orientation,
          hasTankInformation
        );
      },
      phi: () => {
        const config = COLUMN_CONFIG_MAP.phi;
        return createCell(
          'phi',
          phiText,
          config,
          orientation,
          hasTankInformation
        );
      },
      appArea: () => {
        const config = COLUMN_CONFIG_MAP.appArea;
        return createCell(
          'appArea',
          `${convertNumberToDesiredFormat(
            product.appliedAreaValue,
            'fixed'
          )} ${getAbbreviatedUnit(product.appliedAreaUnit)}`,
          config,
          orientation,
          hasTankInformation
        );
      },
      ratePerAc: () => {
        const config = COLUMN_CONFIG_MAP.ratePerAc;
        return createCell(
          'ratePerAc',
          `${convertNumberToDesiredFormat(
            product.ratePerAreaValue,
            'fixed'
          )} ${getAbbreviatedUnit(product.ratePerAreaUnit)}/ac`,
          config,
          orientation,
          hasTankInformation
        );
      },
      ratePerTank: () => {
        const config = COLUMN_CONFIG_MAP.ratePerTank;
        return createCell(
          'ratePerTank',
          `${convertNumberToDesiredFormat(
            product.ratePerTankValue,
            'fixed'
          )} ${getAbbreviatedUnit(product.ratePerTankUnit)}/tank`,
          config,
          orientation,
          hasTankInformation
        );
      },
      totalProduct: () => {
        const config = COLUMN_CONFIG_MAP.totalProduct;
        return createCell(
          'totalProduct',
          `${convertNumberToDesiredFormat(
            product.totalProductValue,
            'fixed'
          )} ${getAbbreviatedUnit(product.totalProductUnit)}`,
          config,
          orientation,
          hasTankInformation
        );
      }
    };

    // Build cells based on column config, filtering out tank column if needed
    const cells = COLUMN_CONFIGS.filter(
      config => hasTankInformation || !config.isTankColumn
    )
      .map(config => cellBuilders[config.key]?.())
      .filter(cell => cell !== undefined);

    return cells;
  });

  return [
    getProductHeaderItems(
      hasTankInformation,
      orientation,
      locale,
      isLatamSpanish
    ),
    ...productRows
  ];
};
